import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle, Shield, Clock, ChevronDown, AlertTriangle, FileText, Wrench, ArrowRight, MapPin } from "lucide-react";
import { DetailedDefects } from "@/components/site/DetailedDefects";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CTASection } from "@/components/site/CTASection";
import voitureIcon from "@/assets/icons/voiture-red.webp";
import calendrierIcon from "@/assets/icons/calendrier-blue.webp";
import { SEOHead } from "@/components/SEOHead";
import { BreadcrumbSchema, FAQSchema } from "@/components/SchemaOrg";
import { ControlPointsInfographic } from "@/components/site/ControlPointsInfographic";
import lexusBanc from "@/assets/centre/lexus-banc.webp";
import golfRBanc from "@/assets/centre/golf-r-banc-controle.webp";
import audiRS6 from "@/assets/centre/audi-rs6-banc.webp";
import lamborghiniUrus from "@/assets/centre/lamborghini-urus-banc.webp";

const vehicleTypes = [
  "Berlines",
  "SUV / Crossover",
  "Citadines",
  "Breaks",
  "Monospaces",
  "Coupés / Cabriolets",
  "4x4",
  "Électriques",
  "Hybrides",
];

const controlPoints = [
  "Identification du véhicule",
  "Freinage",
  "Direction",
  "Visibilité",
  "Éclairage et signalisation",
  "Liaison au sol",
  "Structure et carrosserie",
  "Équipements",
  "Pollution et niveau sonore",
];

const commonDefects = [
  { category: "Éclairage", details: "Phares mal réglés, ampoules grillées, feux de position défaillants, clignotants HS" },
  { category: "Freinage", details: "Plaquettes usées, disques voilés, liquide de frein insuffisant, frein à main inefficace" },
  { category: "Pneus", details: "Usure irrégulière, profondeur de sculpture insuffisante (< 1,6 mm), pression inadaptée" },
  { category: "Pollution", details: "Émissions excessives (diesel : opacité, essence : CO/HC), catalyseur défaillant" },
  { category: "Suspension", details: "Amortisseurs usés, silent-blocs fissurés, rotules de direction avec jeu" },
  { category: "Visibilité", details: "Essuie-glaces usés, pare-brise fissuré dans le champ de vision, rétroviseurs endommagés" },
];

const detailedDefectsData = [
  {
    system: "Système de freinage",
    percent: 22,
    color: "bg-red-500",
    items: [
      { name: "Plaquettes usées", description: "usure normale mais souvent négligée" },
      { name: "Disques voilés", description: "freinage moins efficace, vibrations" },
      { name: "Liquide de frein dégradé", description: "perte d'efficacité progressive" },
      { name: "Flexible de frein poreux", description: "fuite progressive, danger" },
      { name: "Frein à main inefficace", description: "ne tient pas en pente" },
    ],
  },
  {
    system: "Éclairage",
    percent: 33,
    color: "bg-yellow-500",
    items: [
      { name: "Phares mal réglés", description: "éblouissement des autres usagers" },
      { name: "Ampoules grillées", description: "feux arrière, clignotants" },
      { name: "Connectique oxydée", description: "contact intermittent" },
      { name: "Feux de jour défaillants", description: "sécurité réduite" },
    ],
  },
  {
    system: "Pneumatiques",
    percent: 25,
    color: "bg-gray-700",
    items: [
      { name: "Usure irrégulière", description: "géométrie décalée" },
      { name: "Pression insuffisante", description: "consommation carburant augmente" },
      { name: "Craquelures", description: "vieillissement du caoutchouc" },
      { name: "Hernies", description: "risque d'éclatement" },
    ],
  },
  {
    system: "Suspension et direction",
    percent: 15,
    color: "bg-blue-600",
    items: [
      { name: "Amortisseurs usés", description: "confort réduit, sécurité compromise" },
      { name: "Rotules de direction", description: "jeu excessif" },
      { name: "Silent blocs dégradés", description: "bruits de suspension" },
      { name: "Ressorts cassés", description: "affaissement du véhicule" },
    ],
  },
  {
    system: "Pollution",
    percent: 12,
    color: "bg-green-600",
    items: [
      { name: "Filtre à air encrassé", description: "consommation augmente" },
      { name: "Catalyseur défaillant", description: "émissions excessives" },
      { name: "Sonde lambda", description: "mauvaise combustion" },
      { name: "FAP colmaté (diesel)", description: "pollution excessive" },
    ],
  },
  {
    system: "Autres systèmes",
    percent: 8,
    color: "bg-orange-500",
    items: [
      { name: "Essuie-glaces usés", description: "visibilité réduite" },
      { name: "Ceintures bloquées", description: "sécurité compromise" },
      { name: "Rétroviseurs endommagés", description: "angle mort augmente" },
      { name: "Pare-brise fissuré", description: "dans le champ de vision" },
    ],
  },
];

const preparationChecklist = [
  "Vérifier le bon fonctionnement de tous les feux (phares, feux stop, clignotants, feux de recul)",
  "Contrôler le niveau de liquide de frein et de lave-glace",
  "S'assurer que les pneus sont en bon état et correctement gonflés",
  "Vérifier l'état des essuie-glaces (pas de traces, pas de crissement)",
  "Retirer les objets encombrants du coffre pour accéder à la roue de secours",
  "Vérifier que le klaxon fonctionne",
  "S'assurer que les ceintures de sécurité s'enroulent et se déroulent correctement",
  "Apporter la carte grise originale du véhicule",
];

const preparationTimeline = [
  {
    when: "1 semaine avant le CT",
    tasks: [
      "Vérifiez l'éclairage complet (phares, feux arrière, clignotants, feux de jour)",
      "Testez les essuie-glaces (lames usées = refus)",
      "Vérifiez le niveau de liquide de frein",
      "Contrôlez la pression des pneus (voir étiquette sur portière)",
    ],
  },
  {
    when: "2-3 jours avant le CT",
    tasks: [
      "Nettoyez le pare-brise (fissures visibles = refus)",
      "Vérifiez les ceintures de sécurité (doivent se bloquer)",
      "Testez le frein à main (doit tenir en pente)",
      "Vérifiez les rétroviseurs (aucun ne doit manquer)",
    ],
  },
  {
    when: "Le jour du CT",
    tasks: [
      "Arrivez 10 min avant votre RDV",
      "Apportez la carte grise originale",
      "Assurez-vous que le réservoir n'est pas vide",
      "Signalez tout défaut connu (aide le contrôleur)",
    ],
  },
];

const errorsToAvoid = [
  "Arriver avec un réservoir vide (test pollution impossible)",
  "Avoir des pneus dégonflés (refus immédiat)",
  "Oublier la carte grise (impossible de faire le CT)",
  "Arriver avec un pare-brise fissuré (refus)",
  "Négliger l'éclairage (35% des contre-visites)",
];

const VoituresPage = () => {
  return (
    <SiteLayout>
      <SEOHead
        title="CT Voiture dès 73€"
        description="Contrôle technique voiture à Montreuil : 133 points vérifiés, résultat immédiat. Tarif préférentiel web 73€. RDV en ligne, parking gratuit."
        path="/vehicules/voitures"
      />
      <BreadcrumbSchema items={[{ name: "Accueil", href: "/" }, { name: "Véhicules", href: "/prestations" }, { name: "Voitures", href: "/vehicules/voitures" }]} />
      <FAQSchema items={[
        { question: "Quand dois-je faire mon premier CT ?", answer: "Le premier CT est obligatoire dans les 6 mois précédant le 4e anniversaire de la première mise en circulation, puis tous les 2 ans." },
        { question: "Combien coûte le CT voiture ?", answer: "À partir de 73€ en ligne (80€ standard). Hybrides/électriques : 79€. 4x4/GPL : 88€." },
        { question: "Quels défauts courants sont détectés ?", answer: "Éclairage, freins, pneus, pollution, essuie-glaces. 133 points réglementaires contrôlés." },
        { question: "Que se passe-t-il si j'échoue ?", answer: "2 mois pour contre-visite sur les points défaillants. À partir de 30€." },
        { question: "Contrôlez-vous les véhicules électriques ?", answer: "Oui, équipements adaptés. Tarifs à partir de 79€ en ligne." },
        { question: "Combien de temps dure le CT ?", answer: "30 à 45 minutes. Résultat immédiat avec procès-verbal officiel." },
        { question: "Que faire si je roule sans CT valide ?", answer: "Amende de 135€ et immobilisation du véhicule possible." },
        { question: "Quels documents apporter pour le CT voiture ?", answer: "Carte grise originale (certificat d'immatriculation) et permis de conduire. Pour une contre-visite, apportez l'ancien procès-verbal." },
        { question: "Mon véhicule hybride ou électrique peut-il passer le CT chez vous ?", answer: "Oui, notre centre est équipé pour les véhicules hybrides et 100% électriques. Nos contrôleurs sont formés aux spécificités de ces motorisations." },
        { question: "Comment bien préparer ma voiture avant le CT ?", answer: "Vérifiez l'éclairage, les pneus, les essuie-glaces, le niveau de liquide de frein et le klaxon. Ces points simples permettent d'éviter 60% des contre-visites." },
      ]} />
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-autosur-navy to-autosur-blue overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${lexusBanc})` }}
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-body mb-6 border border-white/20">
              <img src={voitureIcon} alt="Voiture" className="w-6 h-6 brightness-0 invert" />
              Catégorie VL
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Contrôle Technique Voiture à Montreuil-sous-Bois
            </h1>
            <p className="font-body text-white/80 text-lg mb-4">
              Votre centre Autosur Montreuil-sous-Bois contrôle tous types de voitures particulières : 
              berlines, SUV, citadines, breaks, 4x4, hybrides et électriques.
            </p>
            <p className="font-body text-white/60 text-sm">
              Inspection automobile complète • Visite technique réglementaire • Diagnostic sécurité véhicule
            </p>
          </motion.div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-6">
                Le contrôle technique automobile : visite technique obligatoire
              </h2>
              <div className="space-y-4 font-body text-muted-foreground">
                <p>
                  Le <strong className="text-autosur-navy">contrôle technique automobile</strong> (aussi appelé visite technique ou inspection automobile) est <strong className="text-autosur-navy">obligatoire</strong> pour 
                  tous les véhicules légers de plus de 4 ans. Il doit être renouvelé tous les 2 ans pour garantir la <strong className="text-autosur-navy">sécurité routière</strong>.
                </p>
                <p>
                  Chez Autosur Montreuil-sous-Bois, nous réalisons le <strong className="text-autosur-navy">diagnostic complet</strong> de l'ensemble des véhicules particuliers, 
                  qu'ils soient thermiques, hybrides ou 100% électriques, avec des équipements homologués et adaptés à chaque motorisation.
                </p>
                <p>
                  Notre équipe de contrôleurs qualifiés et certifiés inspecte <strong className="text-autosur-navy">133 points réglementaires</strong> pour 
                  détecter tout défaut critique susceptible de compromettre votre sécurité ou celle des autres usagers de la route.
                </p>
              </div>

              {/* Price box */}
              <div className="mt-8 bg-autosur-blue/5 rounded-2xl p-6 border border-autosur-blue/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading font-bold text-autosur-navy text-lg">Contrôle technique VL</p>
                    <p className="font-body text-muted-foreground text-sm">Véhicule essence ou diesel</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-3xl font-bold text-autosur-red">73€</p>
                    <p className="font-body text-muted-foreground text-xs">
                      <span className="line-through">80€</span> À partir de (*)
                    </p>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="mt-4 block w-full py-3 bg-autosur-red text-white font-heading font-semibold text-center rounded-full hover:bg-autosur-red-light transition-colors"
                >
                  Réserver en ligne – Économisez 7€
                </Link>
                <p className="font-body text-muted-foreground text-[10px] mt-2 text-center">(*) Offre non cumulable</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              {/* Types de véhicules */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-heading font-bold text-autosur-navy mb-4">Types de véhicules acceptés</h3>
                <div className="flex flex-wrap gap-2">
                  {vehicleTypes.map((type) => (
                    <span 
                      key={type}
                      className="px-4 py-2 bg-gray-50 rounded-full font-body text-sm text-autosur-navy"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>

              {/* Points de contrôle */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-heading font-bold text-autosur-navy mb-4">Les 9 familles de points de contrôle</h3>
                <ul className="grid grid-cols-1 gap-2">
                  {controlPoints.map((point) => (
                    <li key={point} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="font-body text-autosur-navy text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Image */}
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={golfRBanc}
                  alt="Golf R sur banc de contrôle technique au centre Autosur Montreuil-sous-Bois"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Défauts courants */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Défauts courants détectés lors du CT voiture
            </h2>
            <p className="font-body text-muted-foreground max-w-2xl mx-auto">
              Voici les anomalies les plus fréquemment constatées lors de l'inspection automobile. 
              Anticiper ces points vous permet d'éviter la contre-visite.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {commonDefects.map((defect, index) => (
              <motion.div
                key={defect.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
              >
                <div className="flex items-center gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <h3 className="font-heading font-bold text-autosur-navy">{defect.category}</h3>
                </div>
                <p className="font-body text-muted-foreground text-sm">{defect.details}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Defects by System */}
      <DetailedDefects
        title="Défauts détectés par système mécanique"
        subtitle="Lors du contrôle technique voiture, nous vérifions 133 points réglementaires regroupés en 9 familles. Voici les défauts les plus courants par système avec leur taux de détection."
        defects={detailedDefectsData}
      />

      {/* 133 Points Infographic */}
      <ControlPointsInfographic />

      {/* Guide de préparation */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
                Comment préparer votre voiture avant le contrôle
              </h2>
              <p className="font-body text-muted-foreground mb-6">
                Une bonne préparation permet d'éviter <strong className="text-autosur-navy">60% des contre-visites</strong>. 
                Suivez cette checklist avant de vous rendre au centre pour votre visite technique automobile.
              </p>
              <ul className="space-y-3">
                {preparationChecklist.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="font-body text-autosur-navy text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={audiRS6}
                  alt="Audi RS6 sur banc de contrôle technique Autosur"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={lamborghiniUrus}
                  alt="Lamborghini Urus en inspection au centre Autosur Montreuil"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enhanced Preparation Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-autosur-red font-heading font-semibold tracking-widest text-sm mb-4 block">
              CHECKLIST COMPLÈTE
            </span>
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Checklist détaillée avant votre CT voiture
            </h2>
            <p className="font-body text-muted-foreground max-w-2xl mx-auto">
              Suivez ce planning étape par étape pour préparer votre véhicule et éviter 60% des contre-visites.
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-6">
            {preparationTimeline.map((step, index) => (
              <motion.div
                key={step.when}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-autosur-blue rounded-full flex items-center justify-center text-white font-heading font-bold text-sm">
                    {index + 1}
                  </div>
                  <h3 className="font-heading font-bold text-autosur-navy">{step.when}</h3>
                </div>
                <ul className="space-y-2 ml-13">
                  {step.tasks.map((task) => (
                    <li key={task} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="font-body text-autosur-navy text-sm">{task}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}

            {/* Errors to avoid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-red-50 rounded-2xl p-6 border border-red-200"
            >
              <h3 className="font-heading font-bold text-autosur-navy mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Erreurs à éviter absolument
              </h3>
              <ul className="space-y-2">
                {errorsToAvoid.map((error) => (
                  <li key={error} className="flex items-start gap-3">
                    <span className="text-red-500 font-bold mt-0.5">✗</span>
                    <span className="font-body text-autosur-navy text-sm">{error}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Après le CT */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-8 text-center">
              Après le contrôle technique : résultats et contre-visite
            </h2>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-autosur-navy mb-2">Résultat favorable (A)</h3>
                    <p className="font-body text-muted-foreground text-sm">
                      Votre véhicule est conforme. Vous recevez un <strong className="text-autosur-navy">procès-verbal officiel</strong> et 
                      la vignette à apposer sur votre pare-brise. Le prochain contrôle est dans 2 ans.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-autosur-navy mb-2">Défaillances majeures (S)</h3>
                    <p className="font-body text-muted-foreground text-sm">
                      Des défauts importants ont été détectés. Vous disposez de <strong className="text-autosur-navy">2 mois</strong> pour 
                      effectuer les réparations et repasser une <strong className="text-autosur-navy">contre-visite</strong> (à partir de 30€) 
                      portant uniquement sur les points défaillants.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Wrench className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-autosur-navy mb-2">Défaillances critiques (R)</h3>
                    <p className="font-body text-muted-foreground text-sm">
                      Le véhicule présente un danger immédiat. Il ne doit plus circuler jusqu'à réparation et 
                      passage d'une contre-visite. Des défauts critiques comme des freins défaillants ou une 
                      direction dangereuse nécessitent une intervention urgente.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cas spécifiques */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Cas spécifiques : véhicules neufs, anciens et électriques
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-heading font-bold text-autosur-navy mb-3">{"Véhicule neuf (< 4 ans)"}</h3>
              <p className="font-body text-muted-foreground text-sm">
                Votre voiture neuve n&apos;a pas besoin de CT avant ses 4 ans. Le premier contrôle doit être 
                effectué dans les <strong className="text-autosur-navy">6 mois précédant le 4e anniversaire</strong> de 
                la date de première immatriculation. N&apos;attendez pas le dernier moment pour{" "}
                <Link to="/contact" className="text-autosur-blue hover:text-autosur-navy transition-colors">prendre rendez-vous</Link>.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-heading font-bold text-autosur-navy mb-3">{"Véhicule ancien (> 10 ans)"}</h3>
              <p className="font-body text-muted-foreground text-sm">
                Les voitures de plus de 10 ans présentent souvent plus de défauts liés à l'usure : 
                corrosion, flexibles usés, jeux de direction. Une <Link to="/blog/premier-controle-technique-4-ans-guide" className="text-autosur-blue hover:text-autosur-navy transition-colors">bonne préparation</Link> est 
                essentielle pour éviter la contre-visite. Pour les véhicules de plus de 30 ans, consultez notre 
                page <Link to="/vehicules/collection" className="text-autosur-blue hover:text-autosur-navy transition-colors">véhicules de collection</Link>.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-heading font-bold text-autosur-navy mb-3">Véhicule électrique / hybride</h3>
              <p className="font-body text-muted-foreground text-sm">
                Le CT des véhicules électriques et hybrides est identique dans sa structure, avec des 
                adaptations pour les spécificités de la motorisation. Notre centre dispose d'équipements 
                homologués et nos contrôleurs sont formés aux particularités de ces véhicules. 
                Tarifs à partir de <strong className="text-autosur-navy">79€ en ligne</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Pourquoi choisir Autosur pour votre CT auto ?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { iconSrc: calendrierIcon, title: "RDV en ligne", desc: "Réservez 24h/24" },
              { icon: Shield, title: "Centre agréé", desc: "Homologation État" },
              { icon: Clock, title: "Rapidité", desc: "30-45 minutes" },
              { icon: CheckCircle, title: "Résultat immédiat", desc: "PV instantané" },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 text-center shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-default"
              >
                <div className="w-12 h-12 mx-auto mb-4 bg-autosur-blue/10 rounded-xl flex items-center justify-center">
                  {item.iconSrc ? (
                    <img src={item.iconSrc} alt={item.title} className="w-7 h-7 object-contain" />
                  ) : item.icon ? (
                    <item.icon className="w-6 h-6 text-autosur-blue" />
                  ) : null}
                </div>
                <h3 className="font-heading font-bold text-autosur-navy mb-2">{item.title}</h3>
                <p className="font-body text-muted-foreground text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-6 text-center">
            Découvrez aussi nos autres contrôles techniques
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8">
            <Link to="/vehicules/motos" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">CT moto & scooter</h3>
              <p className="font-body text-muted-foreground text-xs">Obligatoire depuis 2024 • Dès 70€</p>
            </Link>
            <Link to="/vehicules/utilitaires" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">CT utilitaire</h3>
              <p className="font-body text-muted-foreground text-xs">Fourgons, camping-cars • Dès 79€</p>
            </Link>
            <Link to="/vehicules/collection" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">CT collection</h3>
              <p className="font-body text-muted-foreground text-xs">Véhicules anciens • Dès 73€</p>
            </Link>
            <Link to="/prestations" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">Tous nos tarifs</h3>
              <p className="font-body text-muted-foreground text-xs">Grille tarifaire complète</p>
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl mx-auto">
            <Link to="/blog/premier-controle-technique-4-ans-guide" className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-100 rounded-xl font-body text-autosur-navy text-sm hover:border-autosur-blue/30 hover:bg-autosur-blue/5 transition-all">
              <FileText className="w-4 h-4 text-autosur-blue flex-shrink-0" />
              Premier CT à 4 ans : guide
            </Link>
            <Link to="/blog/economiser-controle-technique-93-astuces" className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-100 rounded-xl font-body text-autosur-navy text-sm hover:border-autosur-blue/30 hover:bg-autosur-blue/5 transition-all">
              <FileText className="w-4 h-4 text-autosur-blue flex-shrink-0" />
              Économiser sur son CT
            </Link>
            <Link to="/faq" className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-100 rounded-xl font-body text-autosur-navy text-sm hover:border-autosur-blue/30 hover:bg-autosur-blue/5 transition-all">
              <FileText className="w-4 h-4 text-autosur-blue flex-shrink-0" />
              Toutes nos FAQ
            </Link>
          </div>

          {/* Nearby cities */}
          <div className="mt-10 text-center">
            <h3 className="font-heading font-bold text-autosur-navy mb-4">CT voiture près de chez vous</h3>
            <div className="flex flex-wrap gap-2 justify-center">
              {["controle-technique-bagnolet", "controle-technique-vincennes", "controle-technique-romainville", "controle-technique-les-lilas", "controle-technique-fontenay-sous-bois", "controle-technique-noisy-le-sec"].map((slug) => (
                <Link
                  key={slug}
                  to={`/${slug}`}
                  className="inline-flex items-center gap-1 px-4 py-2 bg-white border border-gray-200 rounded-full font-body text-autosur-navy text-xs hover:border-autosur-blue/50 hover:bg-autosur-blue/5 transition-all"
                >
                  <MapPin className="w-3 h-3 text-autosur-blue" />
                  {slug.replace("controle-technique-", "").replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-8 text-center">
              Questions fréquentes sur le CT voiture
            </h2>
            <div className="space-y-4">
              {[
                { q: "Quand dois-je faire mon premier CT ?", a: "Le premier contrôle technique est obligatoire dans les 6 mois précédant le 4e anniversaire de la première mise en circulation de votre véhicule. Ensuite, il doit être renouvelé tous les 2 ans. Consultez notre guide complet sur le premier CT." },
                { q: "Combien coûte le CT voiture ?", a: "Le contrôle technique voiture est à partir de 73€ en réservant en ligne (80€ standard). Économisez 7€ en réservant sur notre site. Tarifs spécifiques pour hybrides/électriques (79€) et 4x4/GPL (88€). Consultez notre grille tarifaire complète." },
                { q: "Quels défauts courants sont détectés lors de l'inspection automobile ?", a: "Les anomalies les plus fréquentes lors de la visite technique concernent : l'éclairage (phares mal réglés, ampoules grillées), les freins (plaquettes/disques usés), les pneus (usure irrégulière), la pollution excessive, les essuie-glaces usés et les fuites de liquides." },
                { q: "Que se passe-t-il si j'échoue au contrôle auto ?", a: "Vous avez 2 mois pour effectuer les réparations et repasser une contre-visite portant uniquement sur les points défaillants. Contre-visite client centre à partir de 30€. Si vous dépassez le délai de 2 mois, vous devrez repasser un contrôle technique complet." },
                { q: "Contrôlez-vous les véhicules électriques et hybrides ?", a: "Oui ! Notre centre dispose d'équipements adaptés et notre équipe est formée aux spécificités des véhicules électriques et hybrides rechargeables. Le diagnostic automobile couvre les mêmes points avec des adaptations spécifiques. Tarifs à partir de 79€ en ligne." },
                { q: "Combien de temps dure le CT voiture ?", a: "Un contrôle technique voiture (visite technique) prend généralement 30 à 45 minutes selon le véhicule. Le résultat est immédiat avec délivrance du procès-verbal officiel. Parking gratuit pendant toute la durée du contrôle." },
                { q: "Que faire si je roule sans CT valide ?", a: "Rouler sans contrôle technique valide (inspection automobile périmée) peut entraîner une amende de 135€ et l'immobilisation de votre véhicule. Votre assurance peut également refuser de vous couvrir en cas d'accident. Ne prenez pas ce risque !" },
                { q: "Quels documents apporter pour le CT voiture ?", a: "Apportez la carte grise originale (certificat d'immatriculation). Pour une contre-visite, apportez également l'ancien procès-verbal de contrôle technique. Permis de conduire recommandé mais non obligatoire." },
                { q: "Mon véhicule hybride rechargeable est-il soumis au même contrôle ?", a: "Oui, les véhicules hybrides et électriques sont soumis au même contrôle réglementaire avec 133 points vérifiés. Nos équipements sont adaptés aux motorisations alternatives et nos contrôleurs sont formés à ces technologies." },
                { q: "Comment bien préparer ma voiture avant la visite technique ?", a: "Vérifiez l'éclairage (phares, feux, clignotants), l'état des pneus (usure, pression), les essuie-glaces, le niveau de liquide de frein, le klaxon et les ceintures de sécurité. Ces vérifications simples permettent d'éviter environ 60% des contre-visites." },
              ].map((faq, index) => (
                <details key={index} className="bg-white rounded-xl border border-gray-100 shadow-sm group">
                  <summary className="flex items-center justify-between p-5 cursor-pointer font-heading font-semibold text-autosur-navy text-sm hover:text-autosur-blue transition-colors">
                    {faq.q}
                    <ChevronDown className="w-5 h-5 text-muted-foreground group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
                  </summary>
                  <div className="px-5 pb-5 pt-0">
                    <p className="font-body text-muted-foreground text-sm">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

export default VoituresPage;
